/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import props_contextImg from 'assets/images/props-context.png'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Context API</h2>
    <p className={styles.description}>
      Данные передаются сверху вниз через пропсы, но это может быть неудобно для определенных типов данных (глобальных),
      которые требуются многим компонентам на разных уровнях в приложении (локализация, тема оформления, состояние
      авторизации и др.).{' '}
    </p>
    <p className={styles.description}>
      Контекст обеспечивает способ передачи данных глубоко по дереву компонентов без необходимости явно передавать
      пропсы в промежуточные компоненты вручную на каждом уровне.{' '}
    </p>
    <p className={styles.notificationWarn}>
      Не используйте контекст чтобы избежать передачи пропсов на несколько уровней вниз.{' '}
    </p>
    <img className={styles.img} src={props_contextImg} alt="props-context" />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - React.createContext()</h3>
      <CopyBlock
        text={`const Context = React.createContext(defaultValue);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Создает объект контекста содержащий пару компонентов:{' '}
          <code className={styles.code}>{`<Context.Provider>`}</code> (поставщик) и{' '}
          <code className={styles.code}>{`<Context.Consumer>`}</code> (потребитель).
        </li>
        <li className={styles.list__item}>
          При рендере, потребитель прочитает текущее значение контекста из ближайшего соответствующего поставщика выше в
          дереве компонентов.
        </li>
        <li className={styles.list__item}>
          Аргумент <code className={styles.code}>defaultValue</code> используется потребителем, если у него нет
          соответствующего поставщика над ним в дереве.
        </li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.1 - Свойство displayName</h4>
        <p className={styles.description}>
          Объекту полученному из функции <code className={styles.code}>React.createContext()</code> можно задать
          строковое свойство <code className={styles.code}>displayName</code>. <b>React DevTools</b> использует это
          свойство при отображении контекста.
        </p>
        <p className={styles.description}>
          К примеру, следующий компонент будет отображаться под именем <code className={styles.code}>ThemeContext</code>{' '}
          в <b>DevTools</b>:{' '}
        </p>
        <CopyBlock
          text={`const ThemeContext = React.createContext();
ThemeContext.displayName = 'ThemeContext';

<ThemeContext.Provider> // "ThemeContext.Provider" в DevTools
<ThemeContext.Consumer> // "ThemeContext.Consumer" в DevTools`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Provider</h3>
      <p className={styles.description}>
        Компонент, позволяющий потребителям подписываться на изменения контекста. Используется для создания и передачи
        контекста.{' '}
      </p>
      <CopyBlock text={`<Provider value={/* value */}>`} language="javascript" showLineNumbers={false} theme={ocean} />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Принимает проп <code className={styles.code}>value</code> - значение контекста, которое будет передано
          потомкам-потребителям этого контекста.
        </li>
        <li className={styles.list__item}>
          Позволяет потребителям подписываться на изменения контекста независмо от глубины вложености.
        </li>
        <li className={styles.list__item}>Один провайдер может быть связан со многими потребителями.</li>
        <li className={styles.list__item}>Провайдеры могут быть вложены друг в друга.</li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Consumer</h3>
      <p className={styles.description}>
        Компонент, который подписывается на изменения контекста. Получает текущий контекст из ближайшего сопоставимого{' '}
        <code className={styles.code}>{`<Provider>`}</code> выше в дереве.{' '}
      </p>
      <CopyBlock
        text={`<Consumer>
  {context => {
    /* Возвращает JSX-разметку */
  }}
</Consumer>`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Релизован по патерну <code className={styles.code}>Render Prop</code>, поэтому ожидает функцию в качестве
          дочернего элемента.
        </li>
        <li className={styles.list__item}>
          Параметр <code className={styles.code}>context</code>, будет содержать значение контекста ближайшего
          провайдера для этого контекста выше в дереве.
        </li>
        <li className={styles.list__item}>
          Если для этого контекста нет провайдера, аргумент <code className={styles.code}>context</code> будет содержать
          значение по умолчанию, которое было передано в <code className={styles.code}>React.createContext()</code>.
        </li>
        <li className={styles.list__item}>
          Все потребители, являющиеся потомками провайдера, будут ре-рендериться всякий раз, когда изменяется значение
          контекста (пропа <code className={styles.code}>value</code>).
        </li>
        <li className={styles.list__item}>
          Потребитель обновляется даже тогда, когда компонент-предок выше в дереве отказался ре-рендериваться используя{' '}
          <code className={styles.code}>shouldComponentUpdate()</code>.
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Контекст темы</h3>
      <CopyBlock
        text={`import React, { createContext } from 'react';

const ThemeContext = createContext();

const App = () => (
  <ThemeContext.Provider value="light">
    <div className="App">
      <Toolbar />
    </div>
  </ThemeContext.Provider>
);

const Toolbar = () => (
  <div className="Toolbar">
    <Button label="Log In" />
    <Button label="Log Out" />
  </div>
);

const Button = ({ label }) => (
  <ThemeContext.Consumer>
    {theme => (
      <button
        className={theme === 'light' ? 'btn-light' : 'btn-dark'}
        type="button"
      >
        {label}
      </button>
    )}
  </ThemeContext.Consumer>
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.5 - HOC для подписки на контекст</h3>
      <p className={styles.description}>
        Чаще всего контекст потребляется многими компонентами, и явно оборачивать каждый компонент с помощью{' '}
        <code className={styles.code}>{`<Context.Consumer>`}</code> не лучший подход. Создадим компонент высшего порядка
        для подписки на контекст.
      </p>
      <CopyBlock
        text={`// withTheme.js
import ThemeContext from '/path/to/ThemeContext';

// Эта функция принимает компонент...
const withTheme = WrappedComponent => {
  // ... возвращает другой компонент...
  return function WithTheme(props) {
    // ... который рендерит обернутый в Consumer компонент,
    // передавая тему как проп.
    return (
      <ThemeContext.Consumer>
        {theme => <WrappedComponent {...props} theme={theme} />}
      </ThemeContext.Consumer>
    );
  };
};

export default withTheme;`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Теперь любой компонент, который зависит от контекста темы, может легко подписаться на него с помощью созданной
        нами функции <code className={styles.code}>withTheme</code>.{' '}
      </p>
      <CopyBlock
        text={`import withTheme from '/path/to/withTheme';

const Button = ({ label, theme }) => (
  <button className={theme === 'dark' ? 'btn-dark' : 'btn-light'} type="button">
    {label}
  </button>
);

export default withTheme(Button);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.6 - Производительность</h3>
      <p className={styles.description}>
        Поскольку контекст использует ссылочную идентификацию, чтобы определить, когда нужно проводить ре-рендер,
        существуют некоторые подводные камни, которые могут вызвать лишние отрисовки в потребителях, когда ре-рендерится
        родитель провайдера.{' '}
      </p>
      <p className={styles.description}>
        Например, приведенный ниже код будет повторно отрисовывать всех потребителей каждый раз, когда обновляется
        родитель провайдера, потому что для <code className={styles.code}>value</code> всегда создается новый объект
        (новая ссылка).{' '}
      </p>
      <CopyBlock
        text={`class App extends React.Component {
  render() {
    return (
      <Provider value={{ something: 'some value' }}>
        <Toolbar />
      </Provider>
    );
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Лучший вариант решения этой проблемы - создать отдельный компонент полностью инкапсулирующий всю логику создания
        и управления контекстом. Методы класса так же необходимо поместить в состояние и передавать ссылку на него,
        тогда ре-рендер потребителей будет происходить только тогда, когда изменится состояние провайдера.{' '}
      </p>
      <CopyBlock
        text={`// ThemeContextProvider.js
import React, { Component, createContext } from 'react';

const { Provider, Consumer } = createContext();

export default class ThemeContextProvider extends Component {
  static Consumer = Consumer;

  toggleTheme = () => {
    this.setState(state => ({
      theme: state.theme === 'light' ? 'dark' : 'light',
    }));
  };

  state = {
    theme: 'light',
    toggleTheme: this.toggleTheme,
  };

  render() {
    return <Provider value={this.state}>{this.props.children}</Provider>;
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.7 - Пример локализации статьи</h3>
      <p className={styles.description}>
        Локализация делается при помощи таких библиотек как{' '}
        <a
          className={styles.link}
          href="https://github.com/formatjs/react-intl"
          target="_blank"
          rel="noopener noreferrer"
        >
          react-intl
        </a>{' '}
        или{' '}
        <a
          className={styles.link}
          href="https://github.com/i18next/react-i18next"
          target="_blank"
          rel="noopener noreferrer"
        >
          react-i18next
        </a>
        , которые используют контекст. По примере приведен пример локализации статьи с использованием контекста.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="Render Props example"
        src="https://codesandbox.io/embed/jlk5k0rvow?fontsize=14"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
  </section>
))
